/**
 * useClickOutside Composable
 * 
 * A composable that handles click outside detection for UI elements.
 * It provides functionality to detect clicks outside of a specified element and trigger callbacks.
 * 
 * Features:
 * - Click outside detection
 * - Event listener management
 * - Automatic cleanup
 * - Configurable target element via ref or selector
 * 
 * @param {Ref<boolean>} isOpen - A ref that tracks whether the target element is open/visible
 * @param {Ref<HTMLElement | null>} [containerRef] - Optional ref to the container element
 * @param {string} [selector] - Optional CSS selector for the container (default: '.relative')
 * @returns {Object} An object containing the click outside handler
 */

import { Ref } from 'vue'

export function useClickOutside(
  isOpen: Ref<boolean>,
  containerRef?: Ref<HTMLElement | null>,
  selector?: string
) {
  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as HTMLElement

    // Check against a specific ref element first, fall back to selector
    if (containerRef?.value) {
      if (!containerRef.value.contains(target)) {
        isOpen.value = false
      }
    } else {
      const closestSelector = selector || '.relative'
      if (!target.closest(closestSelector)) {
        isOpen.value = false
      }
    }
  }

  return {
    handleClickOutside
  }
} 